import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "dstill";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../context/GlobalContext";
import { getPrintConnectIndustriesList } from "../../services/api/auth";

export const EprintRegisterFormDetails = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useGlobalContext();
  const { registrationFormData, userInfoResponse, locale, industries, isPreferredStoreFlagEnabled } = state;
  const navigate = useNavigate();
  // Get display label for membership type
  const getMembershipTypeLabel = (value: string) => {
    const options = t("ePrintRegistration.membershipTypeOptions", {
      returnObjects: true,
    }) as Record<string, string>;
    return options?.[value] || value;
  };

  // Get display label for use type
  const getUseTypeLabel = (value: string) => {
    const labels: Record<string, string> = {
      individual: t("ePrintRegistration.individualUse"),
      business: t("ePrintRegistration.businessUse"),
    };
    return labels[value] || value;
  };

  const getPreferredStoreLabel = (value?: string) => value || "";

  const getIndustryLabel = (value?: string) => {
    const selectedIndustry = industries.find(
      (industry) => String(industry.Id) === String(value),
    );

    if (!selectedIndustry) return value || "";

    const isFrenchCulture = locale?.toLowerCase().startsWith("fr");
    return isFrenchCulture
      ? selectedIndustry.IndustryNameFr
      : selectedIndustry.IndustryNameEn;
  };

  useEffect(() => {
    if (
      registrationFormData == undefined ||
      registrationFormData.companyName == "" ||
      registrationFormData.membershipType === "1"
    ) {
      navigate(`/pages/print-connect-registration-form`);
    } else if (userInfoResponse == undefined) {
      //user not authenticated
      window.open("/SessionTimeOut.aspx", "_self");
    }
  }, [registrationFormData, userInfoResponse]);

  useEffect(() => {
    const fetchIndustries = async () => {
      if (industries.length > 0) {
        return;
      }

      const response = await getPrintConnectIndustriesList();
      if (response.data && Array.isArray(response.data)) {
        dispatch({ type: "SET_INDUSTRIES", industries: response.data });
      }
    };

    fetchIndustries();
  }, [industries.length]);

  const handleContinue = () => {
    window.location.href = `/cart?lng=${locale}`;
  };
  return (
    <div className="container mx-auto">
      <div className="px-6 sm:px-8 lg:px-16 lg:py-12 md:py-8 py-6 bg-white max-w-5xl mx-auto">
        <h1 className="text-xl md:text-2xl font-semibold text-charcoal mb-8 text-center">
          {t("ePrintRegistrationDetails.mainTitle")}
        </h1>
        <div className="bg-white border border-light-gray rounded-lg shadow-sm p-6">
          <div className="text-lg font-semibold text-charcoal mb-4">
            {t("ePrintRegistrationDetails.contactDetailsLabel")}
          </div>
          <div className="grid gap-4 lg:gap-6 grid-cols-1 md:grid-cols-2">
            <DetailRow
              key={"contact_details_firstNameLabel"}
              label={t("ePrintRegistrationDetails.firstNameLabel")}
              value={registrationFormData.firstName}
            />
            <DetailRow
              key={"contact_details_lastNameLabel"}
              label={t("ePrintRegistrationDetails.lastNameLabel")}
              value={registrationFormData.lastName}
            />
            <DetailRow
              key={"contact_details_phoneLabel"}
              label={t("ePrintRegistrationDetails.phoneLabel")}
              value={registrationFormData.phoneNumber}
            />
            <DetailRow
              key={"contact_details_emailLabel"}
              label={t("ePrintRegistrationDetails.emailLabel")}
              value={registrationFormData.email}
            />
          </div>
        </div>

        <div className="bg-white border border-light-gray rounded-lg shadow-sm p-6 mt-8">
          <div className="grid gap-4 lg:gap-6 grid-cols-1 md:grid-cols-2">
            <DetailRow
              key={"company_details_companyNameLabel"}
              label={t("ePrintRegistrationDetails.companyNameLabel")}
              value={registrationFormData.companyName}
            />
            <DetailRow
              key={"company_details_industryLabel"}
              label={t("ePrintRegistrationDetails.industryLabel")}
              value={getIndustryLabel(registrationFormData.industry)}
            />
            <DetailRow
              key={"company_details_addressLineOneLabel"}
              label={t("ePrintRegistrationDetails.addressLineOneLabel")}
              value={registrationFormData.addressLineOne}
            />
            <DetailRow
              key={"company_details_addressLineTwoLabel"}
              label={t("ePrintRegistrationDetails.addressLineTwoLabel")}
              value={registrationFormData.addressLineTwo}
            />
            <DetailRow
              key={"company_details_cityLabel"}
              label={t("ePrintRegistrationDetails.cityLabel")}
              value={registrationFormData.cityName}
            />
            <DetailRow
              key={"company_details_provinceLabel"}
              label={t("ePrintRegistrationDetails.provinceLabel")}
              value={registrationFormData.province}
            />
            <DetailRow
              key={"company_details_postalCodeLabel"}
              label={t("ePrintRegistrationDetails.postalCodeLabel")}
              value={registrationFormData.postalCode}
            />
            {isPreferredStoreFlagEnabled && (
            <DetailRow
              key={"company_details_preferredStoreLabel"}
              label={t("ePrintRegistrationDetails.preferredStoreLabel")}
              value={getPreferredStoreLabel(
                registrationFormData.preferredStoreId,
              )}
            />
            )}
            <DetailRow
              key={"company_details_membershipTypeLabel"}
              label={t("ePrintRegistrationDetails.membershipTypeLabel")}
              value={getMembershipTypeLabel(
                registrationFormData.membershipType,
              )}
            />
            {registrationFormData.useType && (
                <DetailRow
                  key={"company_details_useTypeLabel"}
                  label={t("ePrintRegistrationDetails.useTypeLabel")}
                  value={getUseTypeLabel(registrationFormData.useType)}
                />
              )}
            {registrationFormData.numberOfEmployees && (
              <DetailRow
                key={"company_details_numberOfEmployeesLabel"}
                label={t("ePrintRegistrationDetails.numberOfEmployeesLabel")}
                value={registrationFormData.numberOfEmployees}
              />
            )}
            <DetailRow
              key={"company_details_jobTitleLabel"}
              label={t("ePrintRegistrationDetails.jobTitleLabel")}
              value={registrationFormData.jobTitle}
            />
            <DetailRow
              key={"company_details_bdpNumberLabel"}
              label={t("ePrintRegistrationDetails.bdpNumberLabel")}
              value={registrationFormData.bdpNumber || ""}
            />
          </div>
        </div>
        <div className="flex justify-center mt-10">
          {/* <div className="mr-5">
            <Button
              name="editRegistrationDetailsButtonLabel"
              title="Edit Details"
              type="submit"
              color="secondary"
              aria-label={t('ePrintRegistration.editRegistrationDetailsButtonLabel')}
              role="button"
              onClick={() => navigate(`/pages/print-connect-registration-form`)}
            >
              {t('ePrintRegistration.editRegistrationDetailsButtonLabel')}
            </Button>
          </div> */}

          {/* Keep the "View Cart" functionality in this part */}
          <div>
            <Button
              name="viewCartButtonLabel"
              title="View Cart"
              type="submit"
              color="secondary"
              aria-label={t(
                "ePrintRegistration.addRegistrationDetailsGoToCartButtonLabel",
              )}
              role="button"
              onClick={() => handleContinue()}
              style={{
                    color: "white",
                    backgroundColor: "#CC0000",
                    padding: "12px 34px",
                    borderRadius: "4px",
                    transition: "background-color 0.3s ease",
                    cursor: "pointer",
                  }}
            >
              {t(
                "ePrintRegistration.addRegistrationDetailsGoToCartButtonLabel",
              )}
                </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

type DetailRowProps = {
  label: string;
  value: string;
};
const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => {
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-mid-gray">
        {label}
      </span>
      <div className="mt-1">
        <span className="text-base font-medium text-charcoal">
          {value || "-"}
        </span>
      </div>
    </div>
  );
};
